import { ReactNode, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Hand } from "lucide-react"

import InfoIcon from "@/assets/icons/info/regular.svg?react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ds/Tooltip"
import { Typography, typographyVariants } from "@/components/ds/Typography"
import { cn } from "@/lib/utils"

interface HelpingHandTooltipProps {
  children: ReactNode
  className?: string
  side?: "top" | "bottom" | "left" | "right"
}

/**
 * A reusable helping hand tooltip component that displays contextual help
 * information in a tooltip with a hand icon and custom content.
 *
 * - Click the info icon to open/close the tooltip.
 * - Clicking outside the tooltip or trigger closes it.
 *
 * @example
 * ```tsx
 * <HelpingHandTooltip>
 *   <Typography variant="body1">
 *     This is helpful information about this field.
 *   </Typography>
 * </HelpingHandTooltip>
 * ```
 */
export function HelpingHandTooltip({ children, className, side = "bottom" }: HelpingHandTooltipProps) {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target) return

      const clickedInsideContainer = containerRef.current?.contains(target)
      const clickedInsideContent = contentRef.current?.contains(target)

      if (!clickedInsideContainer && !clickedInsideContent) {
        setOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  return (
    <div className="flex items-center" ref={containerRef}>
      <Tooltip open={open}>
        <TooltipTrigger onMouseEnter={() => []} onMouseLeave={() => []} onClick={() => setOpen((prev) => !prev)}>
          <InfoIcon className={cn("h-5 w-5 cursor-pointer text-brand-primary", className)} />
        </TooltipTrigger>
        <TooltipContent ref={contentRef} side={side} className="relative w-[330px] rounded-2xl bg-neutral-title p-3">
          <div className="flex h-8 w-fit items-center gap-2 rounded-full bg-white/10 p-1 px-2 py-1">
            <Hand className="h-5 w-5 text-neutral-mainBg" />
            <Typography variant="body2" className="font-medium text-neutral-mainBg">
              {t("helpingHand")}
            </Typography>
          </div>
          <div
            className={cn(
              "mt-4 h-full w-full space-y-6 rounded-xl bg-neutral-title p-0 text-neutral-mainBg shadow-2xl",
              typographyVariants({ variant: "body1" }),
            )}
          >
            {children}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

export default HelpingHandTooltip
